"use strict";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { isFunction, isArray } from "lodash";
import { Transition } from "react-transition-group";
import { Stack } from "../Stack/Stack";
import { Spinner } from "../Spinner/Spinner";
import { Text } from "../Text";
import Panel from "../Panel/Panel";
import { Close } from "../../icons";
import {
  KeyType,
  Layer,
  OnClickOutside,
  getPortalRoot,
  forceReflow,
  useOverlayPosition,
  randomString
} from "../../../utils";
import * as styles from "./Combobox-css";
import { MenuItem } from "../MenuItem/MenuItem";
import { duration } from "../../../tokens/tokens-css";
function Combobox(props) {
  const id = useRef(`combobox-${randomString()}`);
  const classes = useRef({
    wrapper: `${id.current}`,
    listBox: `${id.current}-listBox`
  });
  const {
    value,
    onChange,
    getOptionLabel,
    getOptionKey,
    renderInput,
    onInputChange,
    isOptionDisabled,
    renderOption,
    renderClearButton,
    renderListBoxButton,
    noOptionsText
  } = props;
  const [inputValue, _setInputValue] = useState(void 0);
  const setInputValue = useCallback(
    (value2) => {
      _setInputValue(value2);
      if (onInputChange) {
        onInputChange(value2 != null ? value2 : "");
      }
    },
    [onInputChange]
  );
  const [isOpen, setIsOpen] = useState(false);
  const {
    overlayProps = {
      overlay: {
        top: 0,
        left: 0
      },
      width: 400
    },
    overlayRef,
    targetRef,
    targetElement
  } = useOverlayPosition({
    offset: 8
  });
  const focusInput = useCallback(() => {
    if (targetElement) {
      targetElement.focus();
    }
  }, [targetElement]);
  const close = useCallback(() => {
    setInputValue(void 0);
    setIsOpen(false);
    if (targetElement) {
      targetElement.blur();
    }
  }, [setInputValue, setIsOpen, targetElement]);
  const onKeyDown = useCallback(
    (event) => {
      if (event.key === KeyType.Escape) {
        close();
      }
    },
    [close]
  );
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const outsideClick = new OnClickOutside([`.${classes.current.wrapper}`, `.${classes.current.listBox}`], close);
    outsideClick.startListening();
    document.addEventListener("keydown", onKeyDown);
    return () => {
      outsideClick.stopListening();
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onKeyDown, close, classes]);
  let options = [];
  let isLoading = false;
  if (isFunction(props.options)) {
    options = props.options(inputValue != null ? inputValue : "");
  } else if (isArray(props.options)) {
    options = props.options;
  } else if (props.options.isLoading) {
    isLoading = true;
  }
  return /* @__PURE__ */ React.createElement("div", { className: classes.current.wrapper }, renderInput({
    value: inputValue != null ? inputValue : value ? getOptionLabel(value) : "",
    ref: targetRef,
    onChange: setInputValue,
    onFocus: () => {
      setIsOpen(true);
    },
    endElement: renderClearButton && value !== null ? renderClearButton({
      icon: /* @__PURE__ */ React.createElement(Close, null),
      onClick: () => {
        onChange(null);
        setInputValue(void 0);
        focusInput();
      }
    }) : void 0
  }), /* @__PURE__ */ React.createElement(Transition, { mountOnEnter: true, unmountOnExit: true, in: isOpen, timeout: duration.m, onEnter: forceReflow }, (status) => createPortal(
    /* @__PURE__ */ React.createElement(Layer, null, ({ zIndex }) => /* @__PURE__ */ React.createElement(
      Panel,
      {
        className: classNames(classes.current.listBox, styles.listBox({ status })),
        ref: overlayRef,
        style: {
          position: "absolute",
          top: `${overlayProps.overlay.top}px`,
          left: `${overlayProps.overlay.left}px`,
          width: `${overlayProps.width}px`,
          zIndex
        }
      },
      isLoading ? /* @__PURE__ */ React.createElement("div", { className: styles.loader }, /* @__PURE__ */ React.createElement(Spinner, { className: styles.loaderSpinner, primary: true })) : options.length === 0 ? /* @__PURE__ */ React.createElement(Stack, { direction: "vertical", align: "center", padding: "xs" }, /* @__PURE__ */ React.createElement(Text, null, noOptionsText != null ? noOptionsText : "No results found")) : /* @__PURE__ */ React.createElement(Stack, { direction: "vertical", gap: "xxxs", padding: "xs" }, options.map((option) => /* @__PURE__ */ React.createElement(
        MenuItem,
        {
          className: styles.option,
          key: getOptionKey(option),
          size: "s",
          disabled: isOptionDisabled ? isOptionDisabled(option) : false,
          onClick: () => {
            onChange(option);
            close();
          }
        },
        renderOption ? renderOption(option) : getOptionLabel(option)
      )), renderListBoxButton ? renderListBoxButton() : null)
    )),
    getPortalRoot("combobox-root") || document.body
  )));
}
export { Combobox };
